import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { B2BCompany } from 'npx-family-happy-common';
import { B2BCompanyServices } from 'src/app/backoffice/b2b-companies/b2b-company-services/b2b-company.services';
import { Observable, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs';

@Component({
  selector: 'app-b2b-company-select',
  templateUrl: './b2b-company-select.component.html',
  styleUrls: ['./b2b-company-select.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, NgbTypeaheadModule]
})
export class B2BCompanySelectComponent {
  @Input() selectedCompany: B2BCompany | null = null;
  @Output() companySelected = new EventEmitter<B2BCompany | undefined>();
  /**
   * Emits `true` while the input holds text that has not been resolved to a company
   * picked from the dropdown. The parent must block submit while this is `true` so a
   * free-text value can never be persisted as an empty/again B2B link.
   */
  @Output() selectionInvalid = new EventEmitter<boolean>();

  @ViewChild('companyInput') private companyInput?: ElementRef<HTMLInputElement>;

  constructor(private b2bCompanyService: B2BCompanyServices) {}

  search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => this.searchCompanies(term))
    );

  formatter = (company: B2BCompany) => company?.name ?? '';

  private searchCompanies(term: string): Observable<B2BCompany[]> {
    return this.b2bCompanyService.getAllCompanies(0, 100, { name: term, id: null, vatNumber: null, size: null })
      .pipe(
        map(response => response.content)
      );
  }

  onSelect(company: B2BCompany) {
    this.selectedCompany = company;
    this.companySelected.emit(company);
    this.selectionInvalid.emit(false);
  }

  /**
   * With `[editable]="false"` any manual typing sets the bound model to `undefined`,
   * so a keystroke means "no company is selected yet". We only flag the invalid state
   * here (a cheap output the parent turns into a validation error); the actual value
   * is emitted through `onSelect` / `clearSelection`.
   */
  onInput() {
    this.selectionInvalid.emit(this.currentText().length > 0);
  }

  onBlur() {
    const hasUnresolvedText = !this.isCompany(this.selectedCompany) && this.currentText().length > 0;
    this.selectionInvalid.emit(hasUnresolvedText);
  }

  clearSelection() {
    this.selectedCompany = null;
    if (this.companyInput) {
      this.companyInput.nativeElement.value = '';
    }
    this.companySelected.emit(undefined);
    this.selectionInvalid.emit(false);
  }

  private currentText(): string {
    return (this.companyInput?.nativeElement.value ?? '').trim();
  }

  private isCompany(value: unknown): value is B2BCompany {
    return !!value && typeof value === 'object' && 'id' in (value as object);
  }
}
